import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearch, useNavigate } from '@tanstack/react-router';
import client from '@/api/client';
import type { Hostel, RentStatusEntry, PageResponse, RentStatus } from '@/api/types';
import { PendingVerificationsPanel } from '@/features/rent/PendingVerificationsPanel';
import { ReceiptAttachField } from '@/features/rent/ReceiptAttachField';
import { uploadReceipt } from '@/features/rent/receiptApi';
import { Select } from '@/components/ui/Select';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination } from '@/hooks/usePagination';
import { rentStatusLabel, paymentMethodLabel } from '@/lib/labels';

const STATUS_COLORS: Record<RentStatus, string> = {
  PENDING:  'bg-amber-100 text-amber-800',
  PAID:     'bg-green-100 text-green-800',
  PARTIAL:  'bg-blue-100 text-blue-800',
  OVERDUE:  'bg-red-100 text-red-700',
  WAIVED:   'bg-gray-100 text-gray-600',
};

type Tab = 'entries' | 'verifications';
type RentStatusFilter = RentStatus | 'UNPAID' | '';

export function SupervisorRentPage({ role = 'supervisor' }: { role?: 'owner' | 'supervisor' }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  function goToTenant(e: RentStatusEntry) {
    if (role === 'owner') {
      void navigate({
        to: '/owner/hostels/$id/tenants/$tenantId',
        params: { id: e.hostelId, tenantId: e.tenantId },
        search: { tab: 'profile' },
      });
    } else {
      void navigate({
        to: '/supervisor/hostels/$id/tenants/$tenantId',
        params: { id: e.hostelId, tenantId: e.tenantId },
        search: { tab: 'profile' },
      });
    }
  }

  const { data: hostels = [] } = useQuery<Hostel[]>({
    queryKey: ['my-hostels'],
    queryFn: () => client.get('/api/hostels/my').then(r => r as unknown as Hostel[]),
  });

  // Seed initial filters from the URL (e.g. deep-linked from the dashboard); this page is
  // shared by both `/owner/rent` and `/supervisor/rent`, so read search loosely.
  const initialSearch = useSearch({ strict: false }) as Partial<{ hostelId: string; status: string; from: string; to: string }>;

  const [hostelId, setHostelId] = useState(initialSearch.hostelId ?? '');
  const activeHostel = hostelId; // '' = all of the caller's hostels

  const [tab, setTab] = useState<Tab>('entries');
  const [statusFilter, setStatusFilter] = useState<RentStatusFilter>((initialSearch.status as RentStatusFilter) ?? '');
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payingTenantId, setPayingTenantId] = useState<string>('');
  const [payingHostelId, setPayingHostelId] = useState<string>('');
  const [payForm, setPayForm] = useState({ amount: '', paymentMethod: 'CASH', transactionRef: '' });
  const [payReceiptFile, setPayReceiptFile] = useState<File | null>(null);
  const payRefRequired = payForm.paymentMethod === 'UPI' || payForm.paymentMethod === 'BANK_TRANSFER';

  // Date-range filter (due date)
  const [fromDate, setFromDate] = useState(initialSearch.from ?? '');
  const [toDate, setToDate] = useState(initialSearch.to ?? '');

  const { page, size, setPage, resetPage } = usePagination();

  const { data, isLoading } = useQuery<PageResponse<RentStatusEntry>>({
    queryKey: ['rent', activeHostel, statusFilter, fromDate, toDate, page],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), size: String(size) });
      if (activeHostel) p.set('hostelId', activeHostel);
      if (statusFilter === 'UNPAID') p.set('unpaidOnly', 'true');
      else if (statusFilter) p.set('status', statusFilter);
      if (fromDate) p.set('from', fromDate);
      if (toDate) p.set('to', toDate);
      return client.get(`/api/rent?${p}`).then(r => r as unknown as PageResponse<RentStatusEntry>);
    },
  });

  const payMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: { amount: number; paymentMethod: string; transactionRef?: string } }) => {
      let receiptUrl: string | undefined;
      if (payReceiptFile) {
        receiptUrl = await uploadReceipt(payReceiptFile, payingHostelId, payingTenantId);
      }
      return client.post(`/api/rent/${id}/pay`, { ...body, receiptUrl });
    },
    onSuccess: () => {
      setPayingId(null);
      setPayReceiptFile(null);
      void qc.invalidateQueries({ queryKey: ['rent'] });
    },
  });

  const entries = data?.content ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('rent.title')}</h1>
        <div className="flex flex-wrap items-end gap-2">
          {hostels.length > 1 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('hostel.hostelName')}</label>
              <Select
                value={hostelId}
                onChange={e => { setHostelId(e.target.value); resetPage(); }}
                className="w-auto h-9"
                options={[
                  { value: '', label: t('dashboard.allHostels') },
                  ...hostels.map(h => ({ value: h.id, label: h.name })),
                ]}
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('owner.fromLabel', 'From')}</label>
            <input
              type="date"
              value={fromDate}
              onChange={e => { setFromDate(e.target.value); resetPage(); }}
              className="h-9 border border-gray-300 rounded-lg px-3 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('owner.toLabel', 'To')}</label>
            <input
              type="date"
              value={toDate}
              onChange={e => { setToDate(e.target.value); resetPage(); }}
              className="h-9 border border-gray-300 rounded-lg px-3 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.status')}</label>
            <Select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value as RentStatusFilter); resetPage(); }}
              className="w-auto h-9"
              options={[
                { value: '', label: t('common.all') },
                { value: 'UNPAID', label: t('dashboard.unpaid') },
                ...(['PENDING', 'PAID', 'PARTIAL', 'OVERDUE', 'WAIVED'] as RentStatus[]).map(s => ({ value: s, label: rentStatusLabel(t, s) })),
              ]}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100">
          {([
            { key: 'entries', label: t('owner.rentEntries', 'Rent Entries') },
            { key: 'verifications', label: t('owner.pendingVerifications', 'Pending Verifications') },
          ] as { key: Tab; label: string }[]).map(tabItem => (
            <button
              key={tabItem.key}
              onClick={() => setTab(tabItem.key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === tabItem.key
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        {tab === 'entries' && (
          <>
            {isLoading ? (
              <div className="p-10 text-center">
                <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-gray-400 text-sm">{t('common.loading')}</p>
              </div>
            ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">{t('supervisor.tenant', 'Tenant')}</th>
                  <th className="px-4 py-3 text-left">{t('common.room')}</th>
                  <th className="px-4 py-3 text-left">{t('supervisor.month', 'Month')}</th>
                  <th className="px-4 py-3 text-left">{t('supervisor.due', 'Due')}</th>
                  <th className="px-4 py-3 text-left">{t('supervisor.paid', 'Paid')}</th>
                  <th className="px-4 py-3 text-left">{t('rent.dueDate')}</th>
                  <th className="px-4 py-3 text-left">{t('common.status')}</th>
                  <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map(e => (
                  <tr key={e.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => goToTenant(e)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {e.photoUrl
                          ? <img src={e.photoUrl} className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-200" alt="" />
                          : <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-bold text-sm shrink-0">{e.tenantName.charAt(0).toUpperCase()}</div>
                        }
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{e.tenantName}</p>
                          <p className="text-xs text-gray-400">{e.tenantPhone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{e.roomNumber ?? '—'}</td>
                    <td className="px-4 py-3 font-medium">{e.billingMonth}</td>
                    <td className="px-4 py-3">₹{Number(e.amountDue).toLocaleString()}</td>
                    <td className="px-4 py-3 text-green-700">₹{Number(e.amountPaid).toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-500">{e.dueDate}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[e.status]}`}>{rentStatusLabel(t, e.status)}</span>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={ev => ev.stopPropagation()}>
                      {(e.status === 'PENDING' || e.status === 'PARTIAL' || e.status === 'OVERDUE') && (
                        <button
                          onClick={() => {
                            setPayingId(e.id);
                            setPayingTenantId(e.tenantId);
                            setPayingHostelId(e.hostelId);
                            setPayForm({ amount: String(Number(e.amountDue) - Number(e.amountPaid)), paymentMethod: 'CASH', transactionRef: '' });
                            setPayReceiptFile(null);
                          }}
                          className="text-violet-600 text-xs font-medium hover:underline"
                        >
                          {t('supervisor.collect', 'Collect')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
            {!isLoading && entries.length === 0 && <p className="text-center text-gray-400 py-10">{t('rent.noEntries')}</p>}
            <Pagination
              page={page}
              totalPages={data?.totalPages ?? 0}
              totalElements={data?.totalElements ?? 0}
              size={size}
              onPageChange={setPage}
              className="px-4 pb-3"
            />
          </>
        )}

        {tab === 'verifications' && (
          <PendingVerificationsPanel hostelId={activeHostel} accentColor="violet" />
        )}
      </div>

      {payingId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-chrome-page rounded-xl p-6 w-72 shadow-xl">
            <h3 className="font-semibold mb-4">{t('supervisor.collectPayment', 'Collect Payment')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.amount')} *</label>
                <input type="number" value={payForm.amount} onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('rent.paymentMethod')}</label>
                <Select
                  value={payForm.paymentMethod}
                  onChange={e => setPayForm(p => ({ ...p, paymentMethod: e.target.value }))}
                  options={['CASH', 'UPI', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHEQUE', 'OTHER'].map(m => ({ value: m, label: paymentMethodLabel(t, m) }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('owner.transactionRef', 'Transaction / Reference ID')}{payRefRequired && ' *'}
                </label>
                <input
                  value={payForm.transactionRef}
                  onChange={e => setPayForm(p => ({ ...p, transactionRef: e.target.value }))}
                  placeholder={payRefRequired
                    ? t('owner.upiRefPlaceholderRequired', 'Enter unique UPI / bank transaction ID…')
                    : t('owner.upiRefPlaceholder', 'UPI ID / Ref number…')}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                {payRefRequired && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    {t('owner.transactionRefHint', 'Must be unique — the same reference cannot be used for two payments.')}
                  </p>
                )}
              </div>
              <ReceiptAttachField file={payReceiptFile} onChange={setPayReceiptFile} />
            </div>
            {payMutation.isError && (
              <p className="text-red-500 text-xs mt-2">{(payMutation.error as Error).message}</p>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => payMutation.mutate({
                  id: payingId,
                  body: { amount: Number(payForm.amount), paymentMethod: payForm.paymentMethod, transactionRef: payForm.transactionRef || undefined },
                })}
                disabled={!payForm.amount || (payRefRequired && !payForm.transactionRef.trim()) || payMutation.isPending}
                className="flex-1 bg-violet-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40"
              >{payMutation.isPending ? t('owner.saving', 'Saving…') : t('common.save')}</button>
              <button onClick={() => { setPayingId(null); setPayReceiptFile(null); }} className="flex-1 border rounded-lg py-2 text-sm">{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
